import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import validationSchema from 'src/utils/validationSchema';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';
import { useGetAssigneeList } from 'src/pages/tickets/hooks/useGetAssigneeList';

export interface LineStep3FormValues {
  assignUsers: any[];
  lineChannelId: string;
  lineChannelSecret: string;
  channelName: string;
}

export const useLineStep3 = () => {
  const { state: channelState, handleLineDetail } = useChannelContext();

  const { t } = useTranslate('translation');

  const fldList = [
    { name: 'channelName', required: true },
    { name: 'assignUsers', required: true },
    { name: 'lineChannelId', required: true },
    { name: 'lineChannelSecret', required: true },
  ];

  const CampaignSchema = validationSchema(fldList, t);

  const methods = useForm<LineStep3FormValues>({
    mode: 'all',
    resolver: zodResolver(CampaignSchema),
    defaultValues: {
      channelName: '',
      assignUsers: [],
      lineChannelSecret: '',
      lineChannelId: '',
    },
  });

  const { reset, handleSubmit, watch, setValue } = methods;
  const { onChangeStep } = useChannelContext();

  const {
    _recordList,
    searchValue,
    isFetching,
    open: openList,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
  } = useGetAssigneeList({
    setValue: setValue as any,
    multiFieldAssign: channelState?.assignUsers ? channelState?.assignUsers : [],
  });

  const watchAssignedId = watch('assignUsers');
  const watchLineChannelId = watch('lineChannelId');
  const watchLineChannelName = watch('channelName');
  const watchLineChannelSecret = watch('lineChannelSecret');

  const onSubmit = handleSubmit(() => {
    reset();
    onChangeStep('next');
  });

  useEffect(() => {
    handleLineDetail({
      lineChannelName: watchLineChannelName,
      lineChannelSecret: watchLineChannelSecret,
      lineChannelId: watchLineChannelId,
    });
  }, [watchLineChannelSecret, watchLineChannelId, watchLineChannelName, handleLineDetail]);

  const isNextDisabled = !watchAssignedId || !watchLineChannelSecret || !watchLineChannelId;

  return {
    t,
    methods,
    onSubmit,
    isNextDisabled,
    _recordList,
    searchValue,
    isFetching,
    openList,
    listScrollHandler,
    searchValueHandler,
    openListHandler,
    closeListHandler,
  };
};
